import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { HeaderNav } from "../components/HeaderNav";
import { Footer } from "../components/Footer";
import { Marquee } from "../components/Marquee";
import { RESUME_URL } from "../lib/data";
import { ArrowUpRight, Download, Home, Lock, Info } from "lucide-react";
import { ZoomImage } from "../components/ZoomImage";

const ACCENT = "#F59E0B";
const IMG = "/projects/ftihub";

const reveal = { initial: { opacity: 0, y: 26 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-70px" }, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } };

const MaskLine = ({ children, delay = 0 }) => (
  <span className="block overflow-hidden">
    <motion.span className="block" initial={{ y: "110%" }} animate={{ y: "0%" }} transition={{ duration: 0.9, delay, ease: [0.16, 1, 0.3, 1] }}>{children}</motion.span>
  </span>
);

const Chapter = ({ n, label, title, children }) => (
  <section className="max-w-5xl mx-auto px-6 py-14 border-t border-black/5">
    <motion.div {...reveal} className="grid md:grid-cols-12 gap-8">
      <div className="md:col-span-4">
        <div className="flex items-baseline gap-4">
          <span className="font-serif text-5xl text-primary/20 leading-none">{n}</span>
          <span className="overline text-primary">{label}</span>
        </div>
        {title && <h2 className="font-serif text-3xl md:text-4xl text-foreground mt-5 leading-tight">{title}</h2>}
      </div>
      <div className="md:col-span-7 md:col-start-6 text-lg text-foreground/70 leading-relaxed space-y-4">{children}</div>
    </motion.div>
  </section>
);

const Bullets = ({ items }) => (
  <ul className="space-y-2.5">
    {items.map((p) => (
      <li key={p} className="flex gap-3 text-base"><span className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary/60" /><span>{p}</span></li>
    ))}
  </ul>
);

const Flow = ({ steps }) => (
  <div className="flex flex-wrap items-center gap-2 font-mono text-xs">
    {steps.map((s, i) => (
      <React.Fragment key={s}>
        <span className="rounded-full bg-secondary text-primary px-3 py-1">{s}</span>
        {i < steps.length - 1 && <span className="text-primary/40">→</span>}
      </React.Fragment>
    ))}
  </div>
);

/* ---------- Low-fi wireframe blocks (native SVG, greyscale) ---------- */
const WFrame = ({ title, subtitle, children, note }) => (
  <motion.figure {...reveal} className="group">
    <div className="rounded-2xl bg-white border border-black/10 overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2 bg-secondary/40 border-b border-black/5">
        <div className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-full bg-foreground/25" />
          <span className="h-2 w-2 rounded-full bg-foreground/25" />
          <span className="h-2 w-2 rounded-full bg-foreground/25" />
          <span className="ml-3 font-mono text-[10px] uppercase tracking-widest text-foreground/50">{title}</span>
        </div>
        <span className="font-mono text-[10px] text-foreground/40">Low-fi</span>
      </div>
      <div className="p-4 bg-[#FAFAFA]">
        {children}
      </div>
    </div>
    <figcaption className="mt-3 text-xs text-foreground/60 leading-relaxed">
      <span className="font-mono uppercase tracking-widest text-primary/70 text-[10px] block mb-1">{subtitle}</span>
      {note}
    </figcaption>
  </motion.figure>
);

// A) Territory long-scroll dashboard wireframe
const WireDashboard = () => (
  <svg viewBox="0 0 640 420" className="w-full h-auto">
    <defs>
      <pattern id="dh" width="8" height="8" patternUnits="userSpaceOnUse"><path d="M0 8 L8 0" stroke="#D1D5DB" strokeWidth="1"/></pattern>
    </defs>
    {/* top nav */}
    <rect x="0" y="0" width="640" height="28" fill="#E5E7EB"/>
    <rect x="16" y="9" width="60" height="10" rx="2" fill="#9CA3AF"/>
    <rect x="500" y="9" width="120" height="10" rx="5" fill="#D1D5DB"/>
    {/* territory header */}
    <rect x="16" y="46" width="180" height="14" rx="2" fill="#374151"/>
    <rect x="16" y="66" width="80" height="8" rx="2" fill="#9CA3AF"/>
    <rect x="530" y="46" width="90" height="24" rx="12" fill="#F59E0B"/>
    {/* insight cards */}
    <rect x="16" y="90" width="300" height="80" rx="6" fill="#FFFFFF" stroke="#D1D5DB"/>
    <circle cx="30" cy="106" r="5" fill="#F59E0B"/>
    <rect x="42" y="100" width="120" height="8" rx="2" fill="#4B5563"/>
    <rect x="26" y="120" width="270" height="6" rx="2" fill="#D1D5DB"/>
    <rect x="26" y="132" width="240" height="6" rx="2" fill="#D1D5DB"/>
    <rect x="26" y="150" width="80" height="14" rx="7" fill="#374151"/>

    <rect x="324" y="90" width="300" height="80" rx="6" fill="#FFFFFF" stroke="#D1D5DB"/>
    <circle cx="338" cy="106" r="5" fill="#F59E0B"/>
    <rect x="350" y="100" width="120" height="8" rx="2" fill="#4B5563"/>
    <rect x="334" y="120" width="270" height="6" rx="2" fill="#D1D5DB"/>
    <rect x="334" y="132" width="220" height="6" rx="2" fill="#D1D5DB"/>
    <rect x="334" y="150" width="80" height="14" rx="7" fill="#374151"/>

    {/* charts */}
    <rect x="16" y="184" width="300" height="90" rx="6" fill="#FFFFFF" stroke="#D1D5DB"/>
    <rect x="26" y="194" width="80" height="8" rx="2" fill="#4B5563"/>
    <polyline points="26,258 66,238 106,246 146,220 186,232 226,208 266,222 306,200" fill="none" stroke="#F59E0B" strokeWidth="2"/>
    <polyline points="26,260 66,250 106,248 146,242 186,238 226,232 266,236 306,228" fill="none" stroke="#9CA3AF" strokeWidth="1.5"/>

    <rect x="324" y="184" width="300" height="90" rx="6" fill="#FFFFFF" stroke="#D1D5DB"/>
    <rect x="334" y="194" width="80" height="8" rx="2" fill="#4B5563"/>
    <g transform="translate(334,214)">
      {[40, 28, 46, 22, 34, 18].map((h, i) => (
        <rect key={i} x={i*44} y={60 - h} width="30" height={h} fill={i === 2 ? "#F59E0B" : "#9CA3AF"}/>
      ))}
    </g>

    {/* leaderboard */}
    <rect x="16" y="290" width="608" height="120" rx="6" fill="#FFFFFF" stroke="#D1D5DB"/>
    <rect x="26" y="300" width="120" height="8" rx="2" fill="#4B5563"/>
    <rect x="26" y="318" width="596" height="10" rx="2" fill="url(#dh)"/>
    {[0,1,2,3].map(i => (
      <g key={i} transform={`translate(26,${336+i*18})`}>
        <rect x="0" y="0" width="14" height="12" rx="2" fill="#F59E0B"/>
        <rect x="22" y="2" width="130" height="8" rx="2" fill="#6B7280"/>
        <rect x="170" y="2" width="60" height="8" rx="2" fill="#D1D5DB"/>
        <rect x="240" y="2" width="60" height="8" rx="2" fill="#D1D5DB"/>
        <rect x="310" y="2" width="60" height="8" rx="2" fill="#D1D5DB"/>
        <rect x="380" y="2" width="60" height="8" rx="2" fill="#D1D5DB"/>
        <rect x="450" y="2" width="60" height="8" rx="2" fill="#D1D5DB"/>
        <rect x="520" y="2" width="70" height="8" rx="2" fill="#D1D5DB"/>
      </g>
    ))}
    {/* scroll indicator */}
    <rect x="622" y="120" width="4" height="80" rx="2" fill="#F59E0B" opacity="0.5"/>
  </svg>
);

// B) HCP Insights conversational wireframe
const WireHCP = () => (
  <svg viewBox="0 0 640 420" className="w-full h-auto">
    {/* left rail */}
    <rect x="0" y="0" width="160" height="420" fill="#374151"/>
    <rect x="14" y="20" width="120" height="10" rx="2" fill="#FFFFFF" opacity="0.6"/>
    <rect x="14" y="42" width="80" height="8" rx="2" fill="#FFFFFF" opacity="0.35"/>
    <rect x="14" y="60" width="132" height="34" rx="6" fill="#F59E0B"/>
    <rect x="22" y="72" width="90" height="10" rx="2" fill="#FFFFFF"/>
    <rect x="14" y="110" width="100" height="8" rx="2" fill="#FFFFFF" opacity="0.4"/>
    <rect x="14" y="130" width="130" height="14" rx="7" fill="#FFFFFF" opacity="0.12"/>
    <rect x="14" y="150" width="130" height="14" rx="7" fill="#FFFFFF" opacity="0.12"/>
    {/* right main */}
    <rect x="160" y="0" width="480" height="420" fill="#FFFFFF"/>
    <rect x="176" y="20" width="220" height="14" rx="2" fill="#4B5563"/>
    <rect x="540" y="20" width="80" height="14" rx="4" fill="#F59E0B"/>
    <rect x="176" y="52" width="448" height="1" fill="#E5E7EB"/>
    <rect x="176" y="66" width="448" height="150" rx="6" fill="#F9FAFB" stroke="#E5E7EB"/>
    <rect x="188" y="78" width="120" height="10" rx="2" fill="#6B7280"/>
    {/* chart line + bars */}
    <polyline points="188,180 228,160 268,168 308,140 348,148 388,124 428,138 468,116 508,124" fill="none" stroke="#F59E0B" strokeWidth="2"/>
    <g transform="translate(360,120)">
      {[36,24,42,18,30,14].map((h,i)=>(<rect key={i} x={i*22} y={70-h} width="16" height={h} fill={i===2?"#F59E0B":"#9CA3AF"}/>))}
    </g>
    {/* chat suggestions */}
    <rect x="176" y="228" width="448" height="70" rx="6" fill="#FFFFFF" stroke="#E5E7EB"/>
    <rect x="190" y="242" width="90" height="8" rx="2" fill="#6B7280"/>
    <rect x="190" y="264" width="120" height="20" rx="10" fill="#F3F4F6"/>
    <rect x="316" y="264" width="120" height="20" rx="10" fill="#F3F4F6"/>
    <rect x="442" y="264" width="120" height="20" rx="10" fill="#F3F4F6"/>
    {/* input */}
    <rect x="176" y="316" width="380" height="34" rx="17" fill="#FFFFFF" stroke="#D1D5DB"/>
    <rect x="196" y="330" width="120" height="8" rx="2" fill="#D1D5DB"/>
    <rect x="566" y="316" width="58" height="34" rx="17" fill="#F59E0B"/>
    {/* disclaimer */}
    <rect x="176" y="360" width="300" height="6" rx="2" fill="#E5E7EB"/>
    <rect x="176" y="374" width="200" height="6" rx="2" fill="#E5E7EB"/>
  </svg>
);

// C) NBA Recommendations lifecycle wireframe
const WireNBA = () => (
  <svg viewBox="0 0 640 420" className="w-full h-auto">
    <rect x="0" y="0" width="640" height="420" fill="#FFFFFF"/>
    <rect x="20" y="18" width="180" height="12" rx="2" fill="#374151"/>
    <rect x="20" y="40" width="120" height="8" rx="2" fill="#9CA3AF"/>
    {/* tabs */}
    <rect x="20" y="66" width="80" height="24" rx="4" fill="#F3F4F6"/>
    <rect x="106" y="66" width="120" height="24" rx="4" fill="#F59E0B"/>
    {/* card */}
    <rect x="20" y="102" width="600" height="120" rx="6" fill="#F9FAFB" stroke="#E5E7EB"/>
    <rect x="34" y="114" width="180" height="10" rx="2" fill="#4B5563"/>
    <rect x="540" y="114" width="70" height="10" rx="2" fill="#9CA3AF"/>
    <rect x="34" y="132" width="120" height="8" rx="2" fill="#6B7280"/>
    <rect x="34" y="150" width="550" height="7" rx="2" fill="#D1D5DB"/>
    <rect x="34" y="164" width="530" height="7" rx="2" fill="#D1D5DB"/>
    <rect x="34" y="178" width="380" height="7" rx="2" fill="#D1D5DB"/>
    {/* feedback thumbs */}
    <circle cx="540" cy="200" r="10" fill="#FFFFFF" stroke="#D1D5DB"/>
    <circle cx="570" cy="200" r="10" fill="#FFFFFF" stroke="#D1D5DB"/>
    {/* actions row */}
    <rect x="20" y="240" width="180" height="34" rx="4" fill="#F3F4F6"/>
    <rect x="34" y="253" width="130" height="8" rx="2" fill="#4B5563"/>
    <rect x="440" y="240" width="180" height="34" rx="4" fill="#F3F4F6"/>
    <rect x="454" y="253" width="140" height="8" rx="2" fill="#4B5563"/>
    {/* Q body */}
    <rect x="20" y="290" width="600" height="118" rx="6" fill="#F9FAFB" stroke="#E5E7EB"/>
    <rect x="34" y="302" width="150" height="8" rx="2" fill="#4B5563"/>
    {[0,1,2].map(i=>(
      <g key={i}>
        <circle cx="42" cy={328+i*22} r="3" fill="#F59E0B"/>
        <rect x="54" y={324+i*22} width={520 - i*40} height="7" rx="2" fill="#D1D5DB"/>
      </g>
    ))}
    <circle cx="596" cy="392" r="10" fill="#FFFFFF" stroke="#D1D5DB"/>
    <circle cx="576" cy="392" r="10" fill="#FFFFFF" stroke="#D1D5DB"/>
  </svg>
);

// D) CRM Suggestions list wireframe
const WireCRM = () => (
  <svg viewBox="0 0 640 420" className="w-full h-auto">
    <rect x="0" y="0" width="640" height="420" fill="#FFFFFF"/>
    <rect x="0" y="0" width="640" height="24" fill="#F3F4F6"/>
    <rect x="14" y="8" width="80" height="10" rx="2" fill="#4B5563"/>
    <rect x="220" y="8" width="90" height="10" rx="2" fill="#374151"/>
    {/* orange band */}
    <rect x="0" y="24" width="640" height="4" fill="#F59E0B"/>
    {/* left rail with donuts */}
    <rect x="0" y="28" width="170" height="392" fill="#F9FAFB"/>
    <rect x="30" y="46" width="110" height="10" rx="2" fill="#4B5563"/>
    <circle cx="85" cy="110" r="34" fill="none" stroke="#F59E0B" strokeWidth="6"/>
    <circle cx="85" cy="110" r="34" fill="none" stroke="#374151" strokeWidth="6" strokeDasharray="120 213" transform="rotate(-90 85 110)"/>
    <rect x="60" y="105" width="50" height="10" rx="2" fill="#374151"/>
    <rect x="30" y="160" width="110" height="8" rx="2" fill="#9CA3AF"/>
    <rect x="30" y="176" width="80" height="6" rx="2" fill="#D1D5DB"/>
    <rect x="30" y="188" width="90" height="6" rx="2" fill="#D1D5DB"/>
    <rect x="30" y="220" width="110" height="10" rx="2" fill="#4B5563"/>
    <circle cx="85" cy="280" r="30" fill="none" stroke="#374151" strokeWidth="6"/>
    <rect x="66" y="275" width="38" height="10" rx="2" fill="#374151"/>
    {/* Suggestions list */}
    <rect x="184" y="42" width="150" height="10" rx="2" fill="#F59E0B"/>
    <rect x="184" y="60" width="180" height="8" rx="2" fill="#9CA3AF"/>
    {[0,1,2].map(i=>(
      <g key={i} transform={`translate(184,${82 + i*104})`}>
        <rect width="440" height="94" rx="6" fill="#FFFFFF" stroke="#E5E7EB"/>
        <circle cx="14" cy="18" r="6" fill="#F59E0B"/>
        <rect x="26" y="14" width="180" height="8" rx="2" fill="#F59E0B"/>
        <rect x="14" y="34" width="410" height="6" rx="2" fill="#D1D5DB"/>
        <rect x="14" y="46" width="380" height="6" rx="2" fill="#D1D5DB"/>
        <rect x="14" y="58" width="340" height="6" rx="2" fill="#D1D5DB"/>
        <rect x="14" y="76" width="60" height="8" rx="2" fill="#9CA3AF"/>
        <rect x="330" y="74" width="50" height="12" rx="2" fill="#374151"/>
        <rect x="386" y="74" width="50" height="12" rx="2" fill="#374151"/>
      </g>
    ))}
  </svg>
);

const RESEARCH = [
  ["Territory first, HCP second", "Reps manage many HCPs, so they need a territory-level view before investigating individual profiles."],
  ["Insights must lead to action", "Charts alone show change; AI summaries were used to explain the change, name the contributing HCPs and point to the next investigation."],
  ["Explainability builds trust", "Users needed to see why an insight or recommendation was generated — rationale, sources and dates sit alongside AI outputs."],
  ["Familiar patterns reduce adoption friction", "Recommendation cards borrow the structure of the field team's existing CRM workflow, keeping familiarity while adding AI capability."],
  ["Recommendations require control", "A suggestion may be wrong, mistimed or already done — users can explain, dismiss, snooze, complete, retrieve or ask for an alternative."],
  ["Dense data requires layered disclosure", "Narrative insights, charts and a wide leaderboard could not compete at once — progressive disclosure and overlays kept the surface calm."],
];

const DECISIONS = [
  ["AI insights before raw data", "The dashboard opens with concise territory insights covering class movement and share changes — an interpretation is offered before the detailed chart."],
  ["Evidence without losing context", "Selecting View Evidence opens a contextual overlay with the supporting chart and source dates instead of navigating to a separate report."],
  ["Territory-to-HCP navigation", "HCP references in insights, tooltips and leaderboard rows are interactive — a click opens the individual profile from anywhere in the flow."],
  ["Layered data visualisation", "Market correlation, patient-switching, share-vs-volume and HCP ranking each get their own module; tooltips reveal deeper metrics only on demand."],
  ["A usable high-density leaderboard", "Rank and HCP name stay anchored while metric columns scroll horizontally — filters narrow the visible group, comparison never collapses columns."],
  ["Recommendation lifecycle", "Every card is stateful — explain, feedback, complete, dismiss with reason, snooze, retrieve, previous suggestion, generate an alternative."],
  ["Integrated conversational assistant", "A prompt entry point lives inside the territory experience with suggested starters and a usage disclaimer on generated answers."],
  ["Reusable component system", "Insight cards, KPI tiles, filters, tables, tooltips, overlays, recommendation states and feedback controls were built as one library to scale across brands and therapy areas."],
];

const OUTCOMES = [
  "Connected territory performance with individual HCP action",
  "Reduced the need to interpret multiple disconnected reports",
  "Converted complex data into prioritised narrative insights",
  "Made AI outputs more transparent through rationale and evidence",
  "Improved control over recommendation status and relevance",
  "Supported both broad territory review and direct HCP search",
  "Created reusable patterns for future insight modules",
  "Preserved familiarity with existing field-team workflows",
];

const LEARNINGS = [
  ["Enterprise dashboards need hierarchy, not more data", "Narrative insights established direction; charts and tables provided progressive levels of evidence."],
  ["AI outputs need visible reasoning", "An insight becomes credible when users can inspect its rationale, sources and underlying chart — not just a conclusion."],
  ["Reversibility strengthens user confidence", "Dismiss, snooze, retrieve and previous-suggestion states gave users room to decide without fearing permanent consequences."],
  ["Component states are part of the UX", "Expanded, completed, dismissed and alternate states aren't visual variations — each is a stage in the user's decision."],
  ["Long-scroll needs intentional navigation", "Vertical dashboard scrolling combined with a horizontally scrollable leaderboard preserved readability without hiding data."],
  ["Familiarity and innovation coexist", "Aligning recommendations with a known CRM pattern reduced the learning curve; AI explanations and alternatives added the new value."],
  ["Scalability begins with terminology", "Questions about brand labels, therapy comparisons and HCP naming exposed the importance of components that support future products, not just the first use case."],
];

export default function FieldTerritoryInsightsHubCaseStudy() {
  useEffect(() => {
    document.title = "Field Territory Insights Hub — Case Study · Vatushi Moondra";
    return () => { document.title = "Vatushi Moondra — Senior UX Designer"; };
  }, []);

  return (
    <div className="relative bg-background">
      <HeaderNav overDark />

      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-end overflow-hidden pt-28 pb-16" style={{ background: "#0E1B2A" }}>
        <div className="absolute inset-0" style={{ background: "radial-gradient(60% 65% at 82% 18%, rgba(245,158,11,0.22), transparent 70%)" }} />
        <div className="absolute inset-0 opacity-[0.05]" style={{ backgroundImage: "linear-gradient(#ffffff 1px, transparent 1px), linear-gradient(90deg, #ffffff 1px, transparent 1px)", backgroundSize: "48px 48px" }} />
        <div className="relative z-10 max-w-6xl mx-auto px-6 w-full">
          <div className="flex items-center gap-3 mb-6">
            <span className="font-mono text-[11px] uppercase tracking-[0.25em]" style={{ color: "#FBBF24" }}>Field Territory Insights Hub · Enterprise Analytics</span>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 backdrop-blur px-2.5 py-1 text-[10px] font-mono uppercase tracking-widest text-white/80"><Lock size={10} /> Anonymised</span>
          </div>
          <h1 className="font-serif text-white text-4xl sm:text-6xl lg:text-7xl leading-[1.0] tracking-tight">
            <MaskLine delay={0.15}>From territory data</MaskLine>
            <MaskLine delay={0.28}>to <span className="italic" style={{ color: "#FBBF24" }}>focused HCP</span></MaskLine>
            <MaskLine delay={0.41}>actions.</MaskLine>
          </h1>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.7 }} className="mt-8 max-w-2xl text-white/70 text-lg leading-relaxed">
            Helping pharmaceutical field teams move from territory-level performance data to focused HCP actions — one workflow for insight, evidence and next-best action.
          </motion.p>
        </div>
      </section>

      <div className="border-y border-black/5 bg-secondary/40 py-5 font-mono text-sm uppercase tracking-[0.18em] text-foreground/70">
        <Marquee items={["Enterprise UX", "Data Visualisation", "AI Insight Patterns", "Design System", "Prototyping", "Field-team Workflow"]} />
      </div>

      {/* Meta */}
      <section className="max-w-6xl mx-auto px-6 py-14">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-8">
          {[
            ["Platform", "Enterprise web app"],
            ["Users", "Field reps & territory managers"],
            ["Client", "Global pharmaceutical company"],
            ["Product", "Field Territory Insights Hub"],
            ["Timeline", "3 weeks"],
            ["Role", "Product / UX Designer"],
          ].map(([k, v], i) => (
            <motion.div key={k} {...reveal} transition={{ ...reveal.transition, delay: i * 0.05 }}>
              <div className="overline text-primary/70">{k}</div>
              <div className="text-foreground mt-1.5 leading-snug text-sm">{v}</div>
            </motion.div>
          ))}
        </div>
      </section>

      <Chapter n="01" label="Overview" title="One workflow for territory, HCP, insight and action.">
        <p>Field representatives manage large territories containing hundreds of healthcare professionals. To prepare effectively, they need to understand both the overall territory and the individual HCPs influencing its performance.</p>
        <p>The Field Territory Insights Hub brought together AI-generated territory insights, market and prescription trends, patient-switching behaviour, HCP performance rankings, search and HCP-level intelligence, weekly engagement recommendations and conversational data exploration.</p>
        <p>The product connected territory analysis with individual HCP actions — moving users from <em>“what is changing?”</em> to <em>“who should I engage, and why?”</em></p>
      </Chapter>

      <Chapter n="02" label="Design Problem" title="Data was abundant. Priorities were not.">
        <p>Field teams had access to extensive market, prescription and customer data, but translating that information into clear priorities was hard. The experience needed to help users:</p>
        <Bullets items={[
          "Understand performance across an entire territory",
          "Identify the HCPs driving important changes",
          "Compare multiple metrics without losing context",
          "Verify AI-generated insights through supporting evidence",
          "Move from analysis to a relevant engagement action",
          "Manage recommendations without feeling locked into them",
        ]}/>
        <div className="rounded-2xl text-white p-6 mt-3" style={{ background: "#0E1B2A" }}>
          <div className="overline mb-2" style={{ color: "#FBBF24" }}>How might we</div>
          <p className="font-serif text-xl md:text-2xl leading-snug">…help field teams quickly interpret territory performance, identify priority HCPs and convert insights into confident engagement actions?</p>
        </div>
      </Chapter>

      <Chapter n="03" label="Role & Responsibilities" title="End-to-end product & UX ownership.">
        <Bullets items={[
          "Translating business requirements into user stories",
          "Defining the territory-to-HCP information architecture",
          "Structuring the long-scroll dashboard",
          "Mapping search, insight and recommendation flows",
          "Designing charts, filters and leaderboard interactions",
          "Creating AI insight and evidence patterns",
          "Designing recommendation states and feedback controls",
          "Building reusable components and interaction variants",
          "Prototyping navigation, overlays and state transitions",
          "Collaborating with business and development teams",
        ]}/>
      </Chapter>

      <Chapter n="04" label="Process" title="A component-led enterprise UX process.">
        <Flow steps={["Requirements", "User stories", "IA", "Dashboard structure", "Component exploration", "Hi-fi design", "Prototype", "Stakeholder iteration"]} />
        <p>Two core needs framed the product:</p>
        <Bullets items={[
          "View consolidated insights for all HCPs within a territory.",
          "Receive and manage weekly recommendations for individual HCPs.",
        ]}/>
        <p>The experience was then organised around a progressive journey — <em>Territory overview → Key change → Supporting evidence → Priority HCP → Recommended action.</em></p>
        <p className="text-sm text-foreground/50">Open business questions on brand terminology, therapy-area comparisons, HCP naming conventions and future scalability were documented alongside the designs and considered during iteration.</p>
      </Chapter>

      {/* Research & Insights */}
      <section className="border-t border-black/5 bg-secondary/30 py-16">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div {...reveal} className="max-w-3xl mb-10">
            <div className="flex items-baseline gap-4">
              <span className="font-serif text-5xl text-primary/20 leading-none">05</span>
              <span className="overline text-primary">Research & Key Insights</span>
            </div>
            <h2 className="font-serif text-3xl md:text-5xl text-foreground mt-5 leading-tight">Insights came from the field workflow.</h2>
            <p className="mt-5 text-foreground/60 leading-relaxed">The project file documented requirements and stakeholder questions rather than formal interview findings. The strongest UX insights came from analysing the field representative's daily workflow.</p>
          </motion.div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {RESEARCH.map(([t, d]) => (
              <div key={t} className="rounded-2xl border border-black/5 bg-white p-5 card-shadow">
                <div className="h-1 w-8 rounded-full mb-3" style={{ background: ACCENT }} />
                <h3 className="font-serif text-lg text-foreground leading-snug">{t}</h3>
                <p className="text-sm text-foreground/60 mt-2 leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* USER FLOWS + WIREFRAMES */}
      <section className="border-t border-black/5 py-16" data-testid="ftihub-wireframes">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div {...reveal} className="max-w-3xl mb-10">
            <div className="flex items-baseline gap-4">
              <span className="font-serif text-5xl text-primary/20 leading-none">06</span>
              <span className="overline text-primary">User Flows & Wireframes</span>
            </div>
            <h2 className="font-serif text-3xl md:text-5xl text-foreground mt-5 leading-tight">Structural exploration before pixels.</h2>
            <p className="mt-5 text-foreground/60 leading-relaxed">The working file explored structure and components rather than a separate low-fi set. The wireframes below reconstruct the intent — four flows and four skeleton layouts for the surfaces that ended up in the UI.</p>
          </motion.div>

          {/* flow cards */}
          <div className="grid md:grid-cols-2 gap-4 mb-10">
            {[
              ["Territory exploration", ["My Territory", "Timeframe / filters", "Key insights", "Evidence", "Chart or HCP", "HCP profile"], "Begin with a summary, then progressively access underlying detail."],
              ["HCP discovery", ["Search HCPs & Accounts", "Select HCP", "HCP insights", "Follow-up question"], "Bypass the territory dashboard when the user already knows the HCP."],
              ["Recommendation management", ["Weekly recommendation", "Review action", "View explanation", "Complete / dismiss / alternative"], "Dismissal includes reasons and snoozing; completed or dismissed items can be retrieved."],
              ["Conversational exploration", ["Open assistant", "Suggested prompt or custom Q", "Generated response", "Continue"], "Complements the structured dashboard rather than replacing it."],
            ].map(([t, steps, d]) => (
              <motion.div key={t} {...reveal} className="rounded-2xl bg-white border border-black/5 p-5 card-shadow">
                <div className="overline text-primary/70 mb-3">{t}</div>
                <div className="flex flex-wrap items-center gap-1.5 font-mono text-[11px]">
                  {steps.map((s, i) => (
                    <React.Fragment key={s}>
                      <span className="rounded-full px-2.5 py-0.5" style={{ background: "rgba(245,158,11,0.10)", color: "#B45309" }}>{s}</span>
                      {i < steps.length - 1 && <span className="text-primary/40">→</span>}
                    </React.Fragment>
                  ))}
                </div>
                <p className="text-sm text-foreground/60 mt-3 leading-relaxed">{d}</p>
              </motion.div>
            ))}
          </div>

          {/* wireframe SVGs */}
          <div className="grid md:grid-cols-2 gap-6">
            <WFrame title="Territory dashboard" subtitle="Wireframe A · Long-scroll analytical view"
              note="Territory header → AI insight cards → market / switching charts → high-density HCP leaderboard with anchored name column.">
              <WireDashboard />
            </WFrame>
            <WFrame title="HCP insights & assistant" subtitle="Wireframe B · Conversational HCP view"
              note="HCP profile with prescribing summary, distribution and switching charts. Suggested prompts and a persistent assistant input at the bottom.">
              <WireHCP />
            </WFrame>
            <WFrame title="NBA recommendation" subtitle="Wireframe C · Recommendation lifecycle"
              note="Recommendation card with feedback controls, Explain-the-Suggestions and Alternate-Recommendation actions revealing an evidence body underneath.">
              <WireNBA />
            </WFrame>
            <WFrame title="CRM suggestions list" subtitle="Wireframe D · Familiar CRM pattern"
              note="Left rail counters for scheduled calls and emails; stacked HCP suggestion cards with Dismiss / Mark as Complete affordances borrowed from the existing CRM.">
              <WireCRM />
            </WFrame>
          </div>
        </div>
      </section>

      {/* UI DESIGN — actual mockups from working file */}
      <section className="border-t border-black/5 py-16" style={{ background: "#F5F4F2" }} data-testid="ftihub-ui-section">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div {...reveal} className="max-w-3xl mb-10">
            <div className="flex items-baseline gap-4">
              <span className="font-serif text-5xl text-primary/20 leading-none">07</span>
              <span className="overline text-primary">UI Design & Design Decisions</span>
            </div>
            <h2 className="font-serif text-3xl md:text-5xl text-foreground mt-5 leading-tight">Four surfaces, one connected workflow.</h2>
            <p className="mt-5 text-foreground/60 leading-relaxed">Four hi-fi surfaces cover the territory dashboard, its scrolled analytical view, the integrated conversational assistant, and the weekly Suggestions list. All copy, HCPs and territory names are fictional; only the drug-class terminology is retained for realism.</p>
            <div className="mt-5 inline-flex items-start gap-2 text-xs text-foreground/50 max-w-2xl">
              <Info size={14} className="mt-0.5 shrink-0" />
              <span>Anonymised for a global pharmaceutical client — a fictional brand identity ("Compellium Pharmaceuticals") and territory ("Sarasota") replace the original references.</span>
            </div>
          </motion.div>

          <div className="space-y-8" data-testid="ftihub-ui-slots">
            <motion.figure {...reveal} className="group">
              <div className="rounded-2xl bg-white border border-black/5 overflow-hidden card-shadow">
                <ZoomImage src={`${IMG}/screen-01-territory.png`} alt="Territory Level Insights — Sarasota territory dashboard with AI-generated Drug Class, PSO Market Share and HS Market Share insight cards, prompt suggestions and conversational entry" caption="01 · Territory Level Insights — AI-generated insight cards" className="w-full h-auto block" />
              </div>
              <figcaption className="mt-3 text-xs font-mono uppercase tracking-[0.14em] text-foreground/50">01 · Territory Level Insights — AI-generated insight cards</figcaption>
            </motion.figure>

            <motion.figure {...reveal} className="group">
              <div className="rounded-2xl bg-white border border-black/5 overflow-hidden card-shadow">
                <ZoomImage src={`${IMG}/screen-02-territory-scrolled.png`} alt="Territory Level Insights scrolled — Market & Brand Writing Correlation scatter plot with HCP tooltip, Engagement vs Brand Share radar and Patient Switches by Drug Class matrix" caption="02 · Territory Level Insights — market correlation, engagement radar & switching matrix" className="w-full h-auto block" />
              </div>
              <figcaption className="mt-3 text-xs font-mono uppercase tracking-[0.14em] text-foreground/50">02 · Territory Level Insights — market correlation, engagement radar &amp; switching matrix</figcaption>
            </motion.figure>

            <div className="grid md:grid-cols-2 gap-6">
              <motion.figure {...reveal} className="group">
                <div className="rounded-2xl bg-white border border-black/5 overflow-hidden card-shadow">
                  <ZoomImage src={`${IMG}/screen-03-chat-open.png`} alt="HCP Leaderboard with the conversational assistant open — user has asked to summarise HCP performance, assistant returns a generated response with a company-approved-materials disclaimer" caption="03 · HCP Leaderboard with conversational assistant open" className="w-full h-auto block" />
                </div>
                <figcaption className="mt-3 text-xs font-mono uppercase tracking-[0.14em] text-foreground/50">03 · HCP Leaderboard with conversational assistant open</figcaption>
              </motion.figure>
              <motion.figure {...reveal} className="group">
                <div className="rounded-2xl bg-white border border-black/5 overflow-hidden card-shadow">
                  <ZoomImage src={`${IMG}/screen-04-suggestions.png`} alt="Suggestions tab — weekly recommendation cards per HCP with Suggested Action, Explain Suggestion, Alternate Suggestion, Dismiss and Mark as Complete affordances" caption="04 · Suggestions — weekly recommendation cards with lifecycle actions" className="w-full h-auto block" />
                </div>
                <figcaption className="mt-3 text-xs font-mono uppercase tracking-[0.14em] text-foreground/50">04 · Suggestions — weekly recommendation cards with lifecycle actions</figcaption>
              </motion.figure>
            </div>
          </div>

          {/* Decisions grid */}
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {DECISIONS.map(([t, d], i) => (
              <motion.div key={t} {...reveal} transition={{ ...reveal.transition, delay: (i % 4) * 0.05 }} className="rounded-2xl border border-black/5 bg-white p-5 card-shadow">
                <div className="h-1 w-8 rounded-full mb-3" style={{ background: ACCENT }} />
                <h3 className="font-serif text-lg text-foreground leading-snug">{t}</h3>
                <p className="text-sm text-foreground/60 mt-2 leading-relaxed">{d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* IMPACT */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div {...reveal} className="rounded-3xl text-white p-10 md:p-16 relative overflow-hidden" style={{ background: "#0E1B2A" }}>
            <div className="absolute inset-0" style={{ background: "radial-gradient(60% 60% at 82% 20%, rgba(245,158,11,0.22), transparent 70%)" }} />
            <div className="relative z-10">
              <span className="overline" style={{ color: "#FBBF24" }}>08 · Design Impact</span>
              <h2 className="font-serif text-3xl md:text-5xl mt-4 max-w-3xl leading-tight">Product outcomes, not quantified metrics.</h2>
              <p className="mt-5 max-w-2xl text-white/60 text-sm">The working file did not contain verified production metrics — the impact below is presented as product outcomes rather than business results.</p>
              <div className="mt-8 grid md:grid-cols-2 gap-x-10 gap-y-3 text-white/70">
                {OUTCOMES.map(p => (
                  <div key={p} className="flex gap-3">
                    <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: "#FBBF24" }} />
                    <span className="text-sm leading-relaxed">{p}</span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* LEARNINGS */}
      <section className="border-t border-black/5 py-16">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div {...reveal} className="max-w-3xl mb-10">
            <div className="flex items-baseline gap-4">
              <span className="font-serif text-5xl text-primary/20 leading-none">09</span>
              <span className="overline text-primary">Learnings & Reflections</span>
            </div>
            <h2 className="font-serif text-3xl md:text-5xl text-foreground mt-5 leading-tight">Enterprise dashboards need hierarchy — not more data.</h2>
          </motion.div>
          <div className="grid md:grid-cols-2 gap-4">
            {LEARNINGS.map(([t, d]) => (
              <div key={t} className="rounded-2xl border border-black/5 bg-white p-5 card-shadow">
                <h3 className="font-serif text-lg text-foreground leading-snug">{t}</h3>
                <p className="text-sm text-foreground/60 mt-2 leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-6 mt-6 mb-6 flex flex-wrap items-center justify-between gap-6 border-t border-black/5 pt-10">
        <Link to="/" data-testid="ftihub-home-cta" className="inline-flex items-center gap-2 text-primary hover:underline font-medium"><Home size={16} /> Back to home</Link>
        <div className="flex items-center gap-3">
          <Link to="/#projects" className="inline-flex items-center gap-1.5 rounded-full border border-black/10 bg-white px-5 py-2.5 text-sm text-foreground hover:border-primary hover:text-primary transition-colors">More projects <ArrowUpRight size={15} /></Link>
          <a href={RESUME_URL} target="_blank" rel="noopener noreferrer" download className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-5 py-2.5 text-sm hover:bg-[hsl(242,47%,28%)] transition-colors"><Download size={15} /> Download Resume</a>
        </div>
      </div>
      <Footer />
    </div>
  );
}
